import React, { useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import HEADER_IMG from "../../../asset/preventivi/logo.png";
import NavabarGest from "./navabarGes";

function formatCurrencyEUR(v) {
  const n = isFinite(v) ? Number(v) : 0;
  return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDateIT(d) {
  try {
    if (!d) return "";
    const x = new Date(d);
    const dd = String(x.getDate()).padStart(2, "0");
    const mm = String(x.getMonth() + 1).padStart(2, "0");
    const yyyy = x.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch (_) {
    return d;
  }
}

const emptyRow = () => ({
  descrizione: "",
  quantita: "",
  prezzoUnitario: "",
  iva: "22",
});

export default function PreventivoForm() {
  const [titoloBreve, setTitoloBreve] = useState("");
  const [cliente, setCliente] = useState({
    nome: "",
    cognome: "",
    indirizzo: "",
    telefono: "",
  });
  const [date, setDate] = useState({
    dataPreventivo: "",
    dataFineValidita: "",
  });
  const [righe, setRighe] = useState([emptyRow()]);
  const [note, setNote] = useState("");

  const righeCalcolate = useMemo(() => {
    return righe.map((r) => {
      const q = parseFloat(String(r.quantita).replace(",", ".")) || 0;
      const pu = parseFloat(String(r.prezzoUnitario).replace(",", ".")) || 0;
      const ivaPerc = parseFloat(String(r.iva).replace(",", ".")) || 0;
      const sub = q * pu;
      const totaleIvato = sub * (1 + ivaPerc / 100);
      return { ...r, q, pu, ivaPerc, sub, totaleIvato };
    });
  }, [righe]);

  const totals = useMemo(() => {
    const sommaSub = righeCalcolate.reduce((acc, r) => acc + r.sub, 0);
    const sommaIvato = righeCalcolate.reduce((acc, r) => acc + r.totaleIvato, 0);
    return { sommaSub, sommaIvato };
  }, [righeCalcolate]);

  const addRow = () => setRighe((prev) => [...prev, emptyRow()]);
  const removeRow = (idx) => setRighe((prev) => prev.filter((_, i) => i !== idx));
  const updateRow = (idx, key, val) => {
    setRighe((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  };

  // ✅ Titolo fino a 60 caratteri (finale)
  const titoloPdf = `PREVENTIVO PER ${titoloBreve || ""}`.slice(0, 60);

  const generaPDF = async () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    let cursorY = 40;

    // Header image (opzionale)
    if (HEADER_IMG) {
      try {
        const PAGE_W = pageW;
        const SAFE_MARGIN = 40;
        const imgProps = doc.getImageProperties(HEADER_IMG);
        const fileType = (imgProps && imgProps.fileType) || "PNG";

        const PX_TO_PT = 0.75;
        let imgW = (imgProps?.width || 200) * PX_TO_PT;
        let imgH = (imgProps?.height || 80) * PX_TO_PT;

        const maxW = PAGE_W - SAFE_MARGIN * 2;
        if (imgW > maxW) {
          const scale = maxW / imgW;
          imgW = imgW * scale;
          imgH = imgH * scale;
        }

        const x = (PAGE_W - imgW) / 2;
        doc.addImage(HEADER_IMG, fileType, x, cursorY, imgW, imgH, undefined, "FAST");
        cursorY += imgH + 12;
      } catch (e) {}
    }

    // Dati aziendali
    const azienda = [
      "LNBNDN93A13A745N",
      "via leonardo da vinci n11 Dervio (LC)",
      "p.iva 04109870131 REA:420422",
    ];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    azienda.forEach((line) => {
      doc.text(line, 40, cursorY);
      cursorY += 14;
    });

    // Titolo centrato (con wrapping se serve)
    cursorY += 30;
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");

    const titleLines = doc.splitTextToSize(titoloPdf.toUpperCase(), pageW - 80);
    titleLines.forEach((line) => {
      doc.text(line, pageW / 2, cursorY, { align: "center" });
      cursorY += 18;
    });

    cursorY += 22;

    // Dati cliente + date
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    const bloccoCliente = [
      `${cliente.nome || ""} ${cliente.cognome || ""}`.trim(),
      cliente.indirizzo || "",
      cliente.telefono ? `Tel: ${cliente.telefono}` : "",
    ].filter(Boolean);

    const bloccoDate = [
      `Data preventivo: ${formatDateIT(date.dataPreventivo)}`,
      `Validità fino al: ${formatDateIT(date.dataFineValidita)}`,
    ];

    let yCliente = cursorY;
    bloccoCliente.forEach((line) => {
      doc.text(line, 40, yCliente);
      yCliente += 14;
    });

    let yDate = cursorY;
    bloccoDate.forEach((line) => {
      doc.text(line, pageW - 40, yDate, { align: "right" });
      yDate += 14;
    });

    // Tabella articoli/servizi
    const head = [[
      "Descrizione",
      "Quantità",
      "Prezzo unitario",
      "Subtotale",
      "% IVA",
      "Prezzo ivato",
    ]];

    const body = righeCalcolate.map((r) => [
      r.descrizione || "-",
      String(r.q || 0),
      formatCurrencyEUR(r.pu),
      formatCurrencyEUR(r.sub),
      `${(isFinite(r.ivaPerc) ? r.ivaPerc : 0).toString()}%`,
      formatCurrencyEUR(r.totaleIvato),
    ]);

    autoTable(doc, {
      startY: Math.max(yCliente, yDate) + 12,
      head,
      body,
      styles: {
        font: "helvetica",
        fontSize: 10,
        cellPadding: 6,
        halign: "left",
        textColor: [0, 0, 0],
      },
      headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0] },
      bodyStyles: { textColor: [0, 0, 0] },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
    });

    let y = doc.lastAutoTable.finalY + 16;

    // Riepilogo totali
    doc.setFont("helvetica", "bold");
    doc.text(`Subtotale (imponibile): ${formatCurrencyEUR(totals.sommaSub)}`, pageW - 40, y, {
      align: "right",
    });
    y += 16;
    doc.text(`Totale (IVA inclusa): ${formatCurrencyEUR(totals.sommaIvato)}`, pageW - 40, y, {
      align: "right",
    });

    // ✅ FIRMA PER ACCETTAZIONE: testo + spazio + puntini sotto (come richiesto)
    y += 28;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    // testo
    doc.text("FIRMA PER ACCETTAZIONE:", 40, y);

    // riga vuota (spazio)
    y += 24;

    // puntini sotto
    const buildDots = (targetWidthPt) => {
      let s = "";
      while (doc.getTextWidth(s + ".") < targetWidthPt) s += ".";
      return s;
    };
    doc.text(buildDots(pageW - 80), 40, y);

    // Note aggiuntive (se presenti)
    if (note && note.trim()) {
      y += 28;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Note aggiuntive:", 40, y);
      y += 14;
      const noteLines = doc.splitTextToSize(note.trim(), pageW - 80);
      doc.text(noteLines, 40, y);
    }

    // Salva
    const fileNameSafe = (titoloBreve || "preventivo")
      .toLowerCase()
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/(^-|-$)/g, "");
    doc.save(`${fileNameSafe || "preventivo"}.pdf`);
  };

  // ✅ contatore a 60
  const titoloCounter = `${titoloBreve.length}/60`;

  return (
    <div className="min-h-screen bg-[#173341]">
      <div className="p-4 md:p-6">
        <NavabarGest />

        <div className="mt-4 rounded-2xl bg-[#5b90ab70] p-4 md:p-8 text-white">
          <div className="mx-auto max-w-6xl">
            {/* Header */}
            <div className="mb-6 md:mb-8">
              <h1 className="text-xl md:text-2xl font-semibold">Generatore PDF: Preventivo</h1>
              <p className="text-white/80 text-sm md:text-base">
                Compila il form, aggiungi le righe degli articoli/servizi e genera il PDF del preventivo.
              </p>
            </div>

            {/* Dati generali */}
            <SectionCard title="Dati generali">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">Titolo breve (max 60)</label>
                  <input
                    className="w-full rounded-xl bg-[#5b90ab70] px-3 py-2 outline-none"
                    value={titoloBreve}
                    maxLength={60}
                    onChange={(e) => setTitoloBreve(e.target.value)}
                    placeholder="Es: Rifacimento impianto"
                  />
                  <div className="mt-1 text-right text-xs text-white/70">{titoloCounter}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium">Data preventivo</label>
                    <input
                      type="date"
                      className="w-full rounded-xl bg-[#5b90ab70] px-3 py-2 outline-none"
                      value={date.dataPreventivo}
                      onChange={(e) => setDate({ ...date, dataPreventivo: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Fine validità</label>
                    <input
                      type="date"
                      className="w-full rounded-xl bg-[#5b90ab70] px-3 py-2 outline-none"
                      value={date.dataFineValidita}
                      onChange={(e) => setDate({ ...date, dataFineValidita: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Dati cliente */}
            <div className="mt-4 md:mt-6">
              <SectionCard title="Dati cliente">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium">Nome</label>
                    <input
                      className="w-full rounded-xl bg-[#5b90ab70] px-3 py-2 outline-none"
                      value={cliente.nome}
                      onChange={(e) => setCliente({ ...cliente, nome: e.target.value })}
                    />
                  </div>

                  <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium">Cognome</label>
                    <input
                      className="w-full rounded-xl bg-[#5b90ab70] px-3 py-2 outline-none"
                      value={cliente.cognome}
                      onChange={(e) => setCliente({ ...cliente, cognome: e.target.value })}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium">Indirizzo</label>
                    <input
                      className="w-full rounded-xl bg-[#5b90ab70] px-3 py-2 outline-none"
                      value={cliente.indirizzo}
                      onChange={(e) => setCliente({ ...cliente, indirizzo: e.target.value })}
                    />
                  </div>

                  <div className="md:col-span-1">
                    <label className="mb-1 block text-sm font-medium">Telefono</label>
                    <input
                      className="w-full rounded-xl bg-[#5b90ab70] px-3 py-2 outline-none"
                      value={cliente.telefono}
                      onChange={(e) => setCliente({ ...cliente, telefono: e.target.value })}
                    />
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Righe del preventivo */}
            <div className="mt-4 md:mt-6">
              <SectionCard
                title="Articoli / Servizi"
                right={
                  <button
                    type="button"
                    onClick={addRow}
                    className="w-full sm:w-auto rounded-xl bg-black px-4 py-2 text-white shadow hover:opacity-90"
                  >
                    + Aggiungi riga
                  </button>
                }
              >
                {/* MOBILE: CARD per riga */}
                <div className="md:hidden space-y-3">
                  {righeCalcolate.map((r, idx) => (
                    <div key={idx} className="rounded-2xl bg-[#5b90ab70] p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold">Riga {idx + 1}</div>
                        {righe.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRow(idx)}
                            className="rounded-xl bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700"
                          >
                            Rimuovi
                          </button>
                        )}
                      </div>

                      <div className="mt-3 space-y-3">
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-white/90">
                            Descrizione
                          </label>
                          <textarea
                            className="min-h-[70px] w-full resize-y rounded-xl bg-[#173341] px-3 py-2 outline-none"
                            placeholder="Descrizione articolo/servizio"
                            value={r.descrizione}
                            onChange={(e) => updateRow(idx, "descrizione", e.target.value)}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-white/90">
                              Quantità
                            </label>
                            <input
                              className="w-full rounded-xl bg-[#173341] px-3 py-2 text-right outline-none"
                              inputMode="decimal"
                              placeholder="0"
                              value={r.quantita}
                              onChange={(e) => updateRow(idx, "quantita", e.target.value)}
                            />
                          </div>

                          <div>
                            <label className="mb-1 block text-xs font-semibold text-white/90">
                              % IVA
                            </label>
                            <input
                              className="w-full rounded-xl bg-[#173341] px-3 py-2 text-right outline-none"
                              inputMode="decimal"
                              placeholder="22"
                              value={r.iva}
                              onChange={(e) => updateRow(idx, "iva", e.target.value)}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-semibold text-white/90">
                            Prezzo unitario
                          </label>
                          <input
                            className="w-full rounded-xl bg-[#173341] px-3 py-2 text-right outline-none"
                            inputMode="decimal"
                            placeholder="0,00"
                            value={r.prezzoUnitario}
                            onChange={(e) => updateRow(idx, "prezzoUnitario", e.target.value)}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="rounded-xl bg-[#173341] p-3">
                            <div className="text-xs text-white/70">Subtotale</div>
                            <div className="font-semibold">{formatCurrencyEUR(r.sub)}</div>
                          </div>
                          <div className="rounded-xl bg-[#173341] p-3">
                            <div className="text-xs text-white/70">Prezzo ivato</div>
                            <div className="font-semibold">{formatCurrencyEUR(r.totaleIvato)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* DESKTOP/TABLET: TABELLA */}
                <div className="hidden md:block">
                  <div className="overflow-x-auto rounded-2xl">
                    <table className="w-full min-w-[980px] table-auto text-sm">
                      <thead className="sticky top-0">
                        <tr className="bg-[#5b90ab70] text-left">
                          <th className="p-3">Descrizione</th>
                          <th className="p-3 text-right">Quantità</th>
                          <th className="p-3 text-right">Prezzo unitario</th>
                          <th className="p-3 text-right">Subtotale</th>
                          <th className="p-3 text-right">% IVA</th>
                          <th className="p-3 text-right">Prezzo ivato</th>
                          <th className="p-3 text-right">Azioni</th>
                        </tr>
                      </thead>

                      <tbody>
                        {righeCalcolate.map((r, idx) => (
                          <tr key={idx} className="border-b border-white/10 align-top">
                            <td className="p-3">
                              <textarea
                                className="min-h-[44px] w-full resize-y rounded-xl bg-[#5b90ab70] px-3 py-2 outline-none"
                                placeholder="Descrizione articolo/servizio"
                                value={r.descrizione}
                                onChange={(e) => updateRow(idx, "descrizione", e.target.value)}
                              />
                            </td>

                            <td className="p-3 text-right">
                              <input
                                className="w-full rounded-xl bg-[#5b90ab70] px-3 py-2 text-right outline-none"
                                inputMode="decimal"
                                placeholder="0"
                                value={r.quantita}
                                onChange={(e) => updateRow(idx, "quantita", e.target.value)}
                              />
                            </td>

                            <td className="p-3 text-right">
                              <input
                                className="w-full rounded-xl bg-[#5b90ab70] px-3 py-2 text-right outline-none"
                                inputMode="decimal"
                                placeholder="0,00"
                                value={r.prezzoUnitario}
                                onChange={(e) => updateRow(idx, "prezzoUnitario", e.target.value)}
                              />
                            </td>

                            <td className="p-3 text-right whitespace-nowrap">
                              {formatCurrencyEUR(r.sub)}
                            </td>

                            <td className="p-3 text-right">
                              <input
                                className="w-full rounded-xl bg-[#5b90ab70] px-3 py-2 text-right outline-none"
                                inputMode="decimal"
                                placeholder="22"
                                value={r.iva}
                                onChange={(e) => updateRow(idx, "iva", e.target.value)}
                              />
                            </td>

                            <td className="p-3 text-right whitespace-nowrap">
                              {formatCurrencyEUR(r.totaleIvato)}
                            </td>

                            <td className="p-3 text-right">
                              {righe.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeRow(idx)}
                                  className="rounded-xl bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700"
                                >
                                  Rimuovi
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totali live */}
                <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-end gap-3">
                  <div className="w-full md:w-auto rounded-2xl bg-[#0f2430] p-4">
                    <div className="text-sm text-white/80">
                      Subtotale (imponibile):{" "}
                      <span className="font-semibold text-white">
                        {formatCurrencyEUR(totals.sommaSub)}
                      </span>
                    </div>
                    <div className="mt-1 text-base">
                      Totale (IVA inclusa):{" "}
                      <span className="font-semibold">{formatCurrencyEUR(totals.sommaIvato)}</span>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Note aggiuntive */}
            <div className="mt-4 md:mt-6">
              <SectionCard title="Note aggiuntive (opzionale)">
                <textarea
                  className="min-h-[110px] w-full rounded-xl bg-[#5b90ab70] px-3 py-2 outline-none"
                  placeholder="Eventuali condizioni, tempi, informazioni aggiuntive..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </SectionCard>
            </div>

            {/* Azioni */}
            <div className="sticky bottom-3 md:bottom-4 mt-6 md:mt-8">
              <div className="flex justify-end">
                <button
                  onClick={generaPDF}
                  className="w-full sm:w-auto rounded-2xl bg-black px-6 py-3 text-white shadow-lg hover:opacity-90"
                >
                  Scarica PDF preventivo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, right, children }) {
  return (
    <div className="rounded-2xl bg-[#173341] p-4 md:p-6 shadow-sm">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold">{title}</h2>
        {right ? <div className="sm:flex sm:justify-end">{right}</div> : null}
      </div>
      {children}
    </div>
  );
}
